/* eslint-disable no-param-reassign */
import displayError from 'utils/displayError';
import { $detail, $form, $view } from 'signals/global.signals';
import displayNotification from 'utils/displayNotification';
import getSelectValue from 'utils/getSelectValue';
import { fetchAndSetRoomData } from 'components/views/FounderRoom/_helpers/founderRoom.resolvers';
import { createInvestorOnFounder, deleteInvestorOnFounder } from 'api/founderPipeline.api';
import { getBaseTodos } from 'constants';
import { updateRoom, updateRoomExistingInvestors } from 'api/rooms.api';
import { handleHideModal } from 'components/global/_helpers/global.helpers';
import { getOrgDocuments } from 'api/documents.api';

export async function handleSubmitUpdateRoom() {
  try {
    $view.update({
      isModalLoading: true,
    });
    let filesToInclude = $form.value?.filesToInclude || [];
    if (getSelectValue($form.value?.dataVaultInclusion) === 'all') {
      const allFiles = await getOrgDocuments($detail.value.organizationId);
      filesToInclude = allFiles.data.map((file) => file.id);
    }
    const roomId = $form.value.id;

    // Create update object with only non-null values
    const updateData = {};

    // Helper function to add non-null values
    const addIfNotNull = (key, value) => {
      if (value !== null && value !== undefined) {
        updateData[key] = value;
      }
    };

    addIfNotNull('name', $form.value.name);
    addIfNotNull('dealId', getSelectValue($form.value.dealId));
    addIfNotNull('description', $form.value.description);
    addIfNotNull('minimumInvestment', $form.value.minimumInvestment);
    addIfNotNull('usesOfCapital', $form.value.usesOfCapital);
    addIfNotNull('cap', $form.value.cap);
    addIfNotNull('isMrrIncluded', $form.value.isMrrIncluded);
    addIfNotNull('isArrIncluded', $form.value.isArrIncluded);
    addIfNotNull('isCashOnHandIncluded', $form.value.isCashOnHandIncluded);
    addIfNotNull('isLiabilitiesIncluded', $form.value.isLiabilitiesIncluded);
    addIfNotNull('isActive', $form.value.isActive);
    addIfNotNull('isPublic', $form.value.isPublic);
    addIfNotNull('isAllFilesIncluded', $form.value.isAllFilesIncluded);
    addIfNotNull('filesToInclude', filesToInclude);

    await updateRoom($form.value.id, updateData);

    handleHideModal();
    await fetchAndSetRoomData(roomId);
    displayNotification({
      message: 'Room updated successfully.',
      title: 'Room Update',
      type: 'success',
    });
  } catch (error) {
    displayError({
      message: 'Failed to update room. Please check your information and try again.',
      title: 'Room Update Error',
    });
  } finally {
    $view.update({
      isModalLoading: false,
    });
  }
}

export const handleSubmitAddInvestor = async () => {
  const { newInvestor } = $form.value;
  if (!newInvestor.name || !newInvestor.amount) {
    displayError({
      title: 'Missing Information',
      message: 'Please fill in both the name and amount fields.',
    });
    return;
  }

  try {
    const { existingInvestors, id } = $view.value;
    const updatedInvestors = [...existingInvestors, newInvestor];
    await updateRoomExistingInvestors(id, updatedInvestors);
    $form.update({
      newInvestor: { name: undefined, amount: undefined },
    });
    $view.update({ isModalVisible: false });
    displayNotification({
      title: 'Investor Added',
      message: 'The investor has been added to the room.',
    });
  } catch (error) {
    displayError({
      message: 'Failed to add investor to room. Please try again.',
      title: 'Add Investor Error',
    });
  }
};

export const handleSubmitRemoveInvestor = async () => {
  $view.update({
    isModalLoading: true,
  });
  try {
    await deleteInvestorOnFounder($form.value?.pipelineId);
    // eslint-disable-next-line no-use-before-define
    handleHideRoomModals();
    await fetchAndSetRoomData($detail.value.id);
  } catch (error) {
    displayError({
      message: 'Failed to remove investor from room. Please try again.',
      title: 'Remove Investor Error',
    });
  } finally {
    $view.update({
      isModalLoading: false,
    });
  }
};

export const handleSubmitAddInvestorToRoom = async () => {
  $view.update({
    isModalLoading: true,
  });
  try {
    const { roomId } = $form.value;
    const [recordType, investorId] = getSelectValue($form.value?.investorId).split('-');
    const formData = {
      roomId: Number($form.value.roomId),
      pipelineStatus: getSelectValue($form.value?.pipelineStatus),
      previouslyCommitted: Number($form.value?.previouslyCommitted),
      amountInvested: Number($form.value?.amountInvested),
      investorId: Number(investorId),
      recordType,
      todos: getBaseTodos(),
    };

    await createInvestorOnFounder(formData);
    displayNotification({
      message: 'Investor added to room successfully',
      type: 'success',
    });
    // eslint-disable-next-line no-use-before-define
    handleHideRoomModals();
    await fetchAndSetRoomData(roomId);
  } catch (error) {
    if (error.message === 'Investor already on pipeline') {
      displayNotification({
        message: 'Investor already in pipeline for this room. Cannot add again.',
        variant: 'warning',
        title: 'Validation Error!',
      });
      return;
    }
    displayError({
      message: 'Failed to add investor to room. Please try again.',
      title: 'Add Investor Error',
    });
  } finally {
    $view.update({ isModalLoading: false });
  }
};

export const handleShowRoomModal = (modal, data) => {
  $view.update({
    activeModal: modal,
  });
  $form.update({
    modal: data,
  });
};

export const handleHideRoomModals = () => {
  $view.update({
    activeModal: null,
  });
  $form.update({
    modal: {},
  });
};
